// Small UI icons, drawn as in docs/mockups (ink outlines, currentColor strokes).
import type { LegKind } from '../../shared/pack/schema'
import type { Tab } from '../app/router'

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 } as const

export function TabIcon({ tab }: { tab: Tab }) {
  switch (tab) {
    case 'map':
      return (
        <svg viewBox="0 0 24 24" {...stroke} strokeLinejoin="round" aria-hidden>
          <path d="M12 3l9 5-9 5-9-5z" />
          <path d="M3 13l9 5 9-5" />
        </svg>
      )
    case 'trip':
      return (
        <svg viewBox="0 0 24 24" {...stroke} strokeLinecap="round" aria-hidden>
          <rect x="4" y="5" width="16" height="16" rx="3" />
          <path d="M4 10h16M9 3v4M15 3v4" />
        </svg>
      )
    case 'learn':
      return (
        <svg viewBox="0 0 24 24" {...stroke} strokeLinejoin="round" aria-hidden>
          <path d="M3 5.5c3-1.5 6-1.5 9 .5v14c-3-2-6-2-9-.5zM21 5.5c-3-1.5-6-1.5-9 .5v14c3-2 6-2 9-.5z" />
        </svg>
      )
    case 'arcade':
      return (
        <svg viewBox="0 0 24 24" {...stroke} aria-hidden>
          <circle cx="12" cy="9" r="6" />
          <path d="M6 9h12M7 15h10v6H7z" />
        </svg>
      )
    case 'crate':
      return (
        <svg viewBox="0 0 24 24" {...stroke} strokeLinejoin="round" aria-hidden>
          <rect x="3" y="10" width="18" height="10" rx="2" />
          <path d="M5 10l2-5h10l2 5M10 14h4" />
        </svg>
      )
    case 'organiser':
      // A clipboard with a tick (WI-19): Organiser home, the grown-up's tab.
      return (
        <svg viewBox="0 0 24 24" {...stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="5" y="4" width="14" height="17" rx="2.5" />
          <path d="M9 3h6v3H9zM8.5 13l2.5 2.5 4.5-5" />
        </svg>
      )
  }
}

export function SnowflakeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2F7BFF" strokeWidth="3" strokeLinecap="round" aria-hidden>
      <path d="M12 2v20M3.5 7l17 10M20.5 7l-17 10" />
    </svg>
  )
}

/** Toy snowboarder head (the player). */
export function PlayerFace({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="-12 -14 24 24" aria-hidden>
      <circle cx="0" cy="-2" r="8" fill="#FFE2C4" stroke="#1B2A3A" strokeWidth="2.5" />
      <path d="M-8.5,-4 a8.5,8.5 0 0 1 17,0z" fill="#FF4F4F" stroke="#1B2A3A" strokeWidth="2.5" />
      <rect x="-6.5" y="-6" width="13" height="4.5" rx="2.2" fill="#43C6DB" stroke="#1B2A3A" strokeWidth="1.5" />
    </svg>
  )
}

/** Clipboard (the organiser). */
export function OrganiserIcon({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden>
      <rect x="12" y="10" width="40" height="48" rx="8" fill="#FFF4E6" stroke="#1B2A3A" strokeWidth="4" />
      <rect x="22" y="5" width="20" height="11" rx="4" fill="#FFC93C" stroke="#1B2A3A" strokeWidth="3.5" />
      <g stroke="#1B2A3A" strokeWidth="4" strokeLinecap="round">
        <path d="M21 28h22M21 37h22M21 46h13" />
      </g>
      <path d="M40,46 l4,4 l8,-10" fill="none" stroke="#1FA874" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/** Toy snowboarder, full body. */
export function Snowboarder({ size = 72 }: { size?: number }) {
  return (
    <svg width={size} height={size * 1.1} viewBox="-30 -42 60 64" aria-hidden>
      <ellipse cx="0" cy="16" rx="22" ry="5" fill="#1B2A3A" opacity=".25" />
      <g transform="rotate(-10)">
        <rect x="-22" y="7" width="44" height="8" rx="4" fill="#FF4F4F" stroke="#1B2A3A" strokeWidth="2.5" />
        <path d="M-7,8 l2,-13 h10 l2,13z" fill="#2F7BFF" stroke="#1B2A3A" strokeWidth="2.5" strokeLinejoin="round" />
        <rect x="-10" y="-22" width="20" height="19" rx="7" fill="#FFC93C" stroke="#1B2A3A" strokeWidth="2.5" />
        <circle cx="0" cy="-30" r="9" fill="#FFE2C4" stroke="#1B2A3A" strokeWidth="2.5" />
        <path d="M-9.5,-32 a9.5,9.5 0 0 1 19,0z" fill="#FF4F4F" stroke="#1B2A3A" strokeWidth="2.5" />
        <rect x="-7" y="-34" width="14" height="5" rx="2.5" fill="#43C6DB" stroke="#1B2A3A" strokeWidth="1.5" />
      </g>
    </svg>
  )
}

export function LegIcon({ kind }: { kind: LegKind }) {
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', stroke: '#1B2A3A', strokeWidth: 2.2, strokeLinejoin: 'round' as const, 'aria-hidden': true }
  switch (kind) {
    case 'flight':
      return (
        <svg {...common} fill="#2F7BFF" strokeWidth={1.8}>
          <path d="M2 13l3-1 4 2 4-9 3-1-2 10 6 3v2l-7-1-3 4h-2l1-5-5-1-2 2H1z" />
        </svg>
      )
    case 'bus':
    case 'car':
      return (
        <svg {...common} fill="none">
          <rect x="3" y="4" width="18" height="13" rx="3" fill="#FFC93C" />
          <path d="M3 10h18" />
          <circle cx="7.5" cy="19" r="2" fill="#1B2A3A" />
          <circle cx="16.5" cy="19" r="2" fill="#1B2A3A" />
        </svg>
      )
    case 'lift':
      return (
        <svg {...common} fill="none" strokeLinecap="round">
          <path d="M2 5l20 5" />
          <path d="M12 7.5v4" />
          <rect x="7" y="11" width="10" height="9" rx="3" fill="#FF4F4F" />
        </svg>
      )
    default:
      return (
        <svg {...common} fill="none">
          <rect x="5" y="3" width="14" height="14" rx="4" fill="#FF4F4F" />
          <rect x="7.5" y="6" width="9" height="5" rx="1.5" fill="#fff" />
          <path d="M8 21l2-3M16 21l-2-3" strokeLinecap="round" />
        </svg>
      )
  }
}
